// src/pages/Clubs/ListingClubs.jsx
import React, { useState, useEffect } from "react";
import {
  getAllClubsGirondins,
  deleteClubGirondin,
} from "./../../services/clubsgirondinsServices";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import Button from "./../../components/Button/Button";
import MiniClubCard from "./MiniClubCard";
import { ToastContainer, toast } from "react-toastify";
import CreateClubModal from "./CreateClubModal";
import EditClubModal from "./EditClubModal";
import MapComponent from "./../../components/MapComponent/MapComponent";
import { FaFilter, FaRedo, FaSortAlphaDown } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const ListingClubs = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [clubs, setClubs] = useState([]);
  const [filteredClubs, setFilteredClubs] = useState([]);

  const [communes, setCommunes] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [secteurgeo, setSecteurgeo] = useState([]);
  const [disciplines, setDisciplines] = useState([]);

  const [selectedCommune, setSelectedCommune] = useState("");
  const [selectedSecteur, setSelectedSecteur] = useState("");
  const [selectedSecteurGeo, setSelectedSecteurGeo] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [searchText, setSearchText] = useState("");
  const [sortMode, setSortMode] = useState("nameAsc"); // nameAsc | nameDesc | communeAsc

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      const response = await getAllClubsGirondins();
      const clubsWithCoordinates = response.map((club) => {
        const [latitude, longitude] = (club.coordonnees_gps || "")
          .split(",")
          .map((coord) => parseFloat(coord.trim()));
        return { ...club, latitude, longitude };
      });

      setClubs(clubsWithCoordinates);
      setFilteredClubs(clubsWithCoordinates);

      // Listes de filtres
      setCommunes([
        ...new Set(
          clubsWithCoordinates.map((club) => club.commune).filter(Boolean)
        ),
      ]);
      setSecteurs([
        ...new Set(
          clubsWithCoordinates.map((club) => club.secteur).filter(Boolean)
        ),
      ]);
      setSecteurgeo([
        ...new Set(
          clubsWithCoordinates.map((club) => club.secteur_geo).filter(Boolean)
        ),
      ]);

      const allDisciplines = clubsWithCoordinates.flatMap((club) =>
        (club.disciplines || "")
          .split("/")
          .map((d) => d.trim())
          .filter(Boolean)
      );
      setDisciplines([...new Set(allDisciplines)]);
    } catch (error) {
      console.error("Failed to fetch clubs:", error);
      toast.error("Erreur lors du chargement des clubs.");
    }
  };

  const handleAddClub = () => setIsCreateModalOpen(true);

  const handleEditClub = (club) => {
    setSelectedClub(club);
    setIsEditModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    fetchClubs();
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedClub(null);
    fetchClubs();
  };

  const handleDeleteClub = async (clubId) => {
    try {
      await deleteClubGirondin(clubId);
      fetchClubs();
      toast.success("Le club a bien été supprimé.");
    } catch (error) {
      console.error("Failed to delete club:", error);
      toast.error("Une erreur est survenue lors de la suppression du club.");
    }
  };

  const handleResetFilters = () => {
    setSelectedCommune("");
    setSelectedSecteur("");
    setSelectedSecteurGeo("");
    setSelectedDiscipline("");
    setSearchText("");
    setSortMode("nameAsc");
  };

  // Filtres + tri
  useEffect(() => {
    let filtered = [...clubs];

    if (selectedCommune) {
      filtered = filtered.filter((club) => club.commune === selectedCommune);
    }
    if (selectedSecteur) {
      filtered = filtered.filter((club) => club.secteur === selectedSecteur);
    }
    if (selectedSecteurGeo) {
      filtered = filtered.filter(
        (club) => club.secteur_geo === selectedSecteurGeo
      );
    }
    if (selectedDiscipline) {
      filtered = filtered.filter((club) =>
        (club.disciplines || "").includes(selectedDiscipline)
      );
    }
    if (searchText) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(
        (club) =>
          club.nom_club.toLowerCase().includes(q) ||
          club.commune.toLowerCase().includes(q)
      );
    }

    // tri
    filtered.sort((a, b) => {
      const nameA = (a.nom_club || "").toLowerCase();
      const nameB = (b.nom_club || "").toLowerCase();
      const comA = (a.commune || "").toLowerCase();
      const comB = (b.commune || "").toLowerCase();

      switch (sortMode) {
        case "nameDesc":
          return nameB.localeCompare(nameA);
        case "communeAsc":
          return comA.localeCompare(comB) || nameA.localeCompare(nameB);
        case "nameAsc":
        default:
          return nameA.localeCompare(nameB);
      }
    });

    setFilteredClubs(filtered);
  }, [
    selectedCommune,
    selectedSecteur,
    selectedSecteurGeo,
    selectedDiscipline,
    searchText,
    sortMode,
    clubs,
  ]);

  const totalClubs = clubs.length;
  const visibleClubs = filteredClubs.length;

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      <PageHeader
        eyebrow="Utilisateurs & Visiteurs"
        title="Liste des clubs girondins"
        subtitle="Explorez les clubs de Judo et disciplines associées en Gironde : filtrez par commune, secteur, discipline ou recherchez directement un club."
        align="center"
        size="lg"
        className="mb-6 md:mb-8"
      />

      {/* FILTRES */}
      <div className="max-w-[1280px] w-full mx-auto mb-6">
        <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg px-4 sm:px-6 py-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-orange-500">
                <FaFilter />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
                  Filtres & recherche
                </p>
                <p className="text-[11px] text-slate-400 dark:text-slate-400">
                  Affinez l&apos;affichage des clubs selon vos critères.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <FaSortAlphaDown className="text-slate-400" />
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="select select-bordered select-xs sm:select-sm dark:bg-slate-900 dark:border-slate-700 dark:text-white"
              >
                <option value="nameAsc">Nom du club (A → Z)</option>
                <option value="nameDesc">Nom du club (Z → A)</option>
                <option value="communeAsc">Commune (A → Z)</option>
              </select>
              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200/80 dark:border-slate-700/80 px-3 py-1 text-[11px] font-medium text-slate-600 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <FaRedo className="text-[10px]" />
                Reset
              </button>
            </div>
          </div>

          {/* Lignes de filtres */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <select
              value={selectedCommune}
              onChange={(e) => setSelectedCommune(e.target.value)}
              className="select select-bordered w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white"
            >
              <option value="">Toutes les communes</option>
              {communes.map((commune, index) => (
                <option key={index} value={commune}>
                  {commune}
                </option>
              ))}
            </select>

            <select
              value={selectedSecteur}
              onChange={(e) => setSelectedSecteur(e.target.value)}
              className="select select-bordered w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white"
            >
              <option value="">Tous les secteurs</option>
              {secteurs.map((secteur, index) => (
                <option key={index} value={secteur}>
                  {secteur}
                </option>
              ))}
            </select>

            <select
              value={selectedSecteurGeo}
              onChange={(e) => setSelectedSecteurGeo(e.target.value)}
              className="select select-bordered w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white"
            >
              <option value="">Tous les secteurs géographiques</option>
              {secteurgeo.map((s, index) => (
                <option key={index} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              value={selectedDiscipline}
              onChange={(e) => setSelectedDiscipline(e.target.value)}
              className="select select-bordered w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white"
            >
              <option value="">Toutes les disciplines</option>
              {disciplines.map((discipline, index) => (
                <option key={index} value={discipline}>
                  {discipline}
                </option>
              ))}
            </select>
          </div>

          {/* Recherche & compteurs */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
            <div className="relative w-full sm:max-w-xs">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Rechercher par nom ou commune…"
                className="input input-bordered w-full pl-3 pr-3 text-sm dark:bg-slate-900 dark:border-slate-700 dark:text-white"
              />
            </div>

            <div className="flex flex-wrap gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-slate-600 dark:text-slate-200">
                <span className="font-semibold">Total clubs :</span>{" "}
                {totalClubs}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 dark:bg-orange-900/30 px-3 py-1 text-orange-700 dark:text-orange-300">
                <span className="font-semibold">Club(s) affiché(s) :</span>{" "}
                {visibleClubs}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CARTE (inchangée, seulement enveloppée dans une carte) */}
      <div className="max-w-[1280px] w-full mx-auto mb-6 rounded-2xl overflow-hidden border border-slate-200/70 dark:border-slate-700/70 shadow-lg bg-white dark:bg-slate-900">
        <MapComponent clubs={filteredClubs} />
      </div>

      {/* LISTE DES CLUBS */}
      <div className="overflow-x-auto w-full p-4 max-w-[1280px] mx-auto">
        <div className="flex justify-between items-center mb-4">
          {(userRole === "Administrateur" || userRole === "Bureau") && (
            <Button
              text="Ajouter un Club"
              onClick={handleAddClub}
              className="btn-primary"
            />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredClubs.map((club) => (
            <MiniClubCard
              key={club.id}
              club={club}
              handleEditClub={handleEditClub}
              handleDeleteClub={handleDeleteClub}
              refreshList={fetchClubs}
            />
          ))}
        </div>
      </div>

      <CreateClubModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        refreshList={fetchClubs}
      />
      <EditClubModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        refreshList={fetchClubs}
        club={selectedClub}
      />
      <ToastContainer />
    </div>
  );
};

export default ListingClubs;
